import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import Webcam from 'react-webcam';
import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';
import { API_ENDPOINTS, getBrandingLogoUrl } from './config';
import logger from './utils/logger';
import { FiCheckCircle, FiEdit2, FiType, FiCamera, FiX, FiMapPin, FiShield, FiLock } from "react-icons/fi";
import PromotionPage from './PromotionPage';
import SigningLoader from './SigningLoader';
import './App.css';

// Ensure PDF worker is set up
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function SignPage() {
  const { referenceId } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");
  const linkToken = searchParams.get("link"); // Public signing link (open-link request)

  const [docData, setDocData] = useState(null);
  const [openLinkDoc, setOpenLinkDoc] = useState(null);
  const [showOpenLinkForm, setShowOpenLinkForm] = useState(false);
  const [openLinkName, setOpenLinkName] = useState("");
  const [openLinkEmail, setOpenLinkEmail] = useState("");
  const [openLinkClaiming, setOpenLinkClaiming] = useState(false);
  const [openLinkError, setOpenLinkError] = useState("");
  const [pdfUrl, setPdfUrl] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [signingMethod, setSigningMethod] = useState('manual');
  const [isItalic, setIsItalic] = useState(false);
  const [signed, setSigned] = useState(false);

  // New States for Features
  const [isExpired, setIsExpired] = useState(false);
  const [locationCoords, setLocationCoords] = useState({ latitude: null, longitude: null });
  const [locationStatus, setLocationStatus] = useState("pending"); // pending, success, denied
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Face Capture States
  const [showFaceModal, setShowFaceModal] = useState(false);
  const [faceImage, setFaceImage] = useState(null);
  const [faceDetector, setFaceDetector] = useState(null);
  const [isFaceDetected, setIsFaceDetected] = useState(false);
  const [detectionMsg, setDetectionMsg] = useState("Initializing AI...");

  // OTP Verification States
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);

  // Review Page States
  const [showReviewPage, setShowReviewPage] = useState(true); // Start with review page
  const [reviewAccepted, setReviewAccepted] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionError, setRejectionError] = useState('');

  // White-label branding for signing page (fetched by org_id from document)
  const [signPageBranding, setSignPageBranding] = useState({
    product_name: 'SignTada',
    logo_url: null,
    theme: 'default',
    primary_color: '#FFD600',
    accent_color: '#FFAB00',
    show_powered_by: true,
    first_part_color: null,
    rest_part_color: null,
  });

  const webcamRef = useRef(null);
  const signatureRef = useRef(null);
  const detectionInterval = useRef(null);

  // Force light mode on SignPage; restore previous theme on unmount
  useEffect(() => {
    const root = document.documentElement;
    const previousTheme = root.getAttribute('data-theme') || null;
    root.setAttribute('data-theme', 'light');
    return () => {
      if (previousTheme) root.setAttribute('data-theme', previousTheme);
      else root.removeAttribute('data-theme');
    };
  }, []);

  useEffect(() => {
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];

    // --- OPEN LINK FLOW: no token, but link param (public signing link) ---
    if (linkToken && !token) {
      setShowOpenLinkForm(false);
      setOpenLinkDoc(null);
      setOpenLinkError("");
      axios.get(`${baseUrl}/requests/get_document_by_reference/${referenceId}?link=${encodeURIComponent(linkToken)}`)
        .then(response => {
          const doc = response.data;
          setOpenLinkDoc(doc);
          setShowOpenLinkForm(true);
          if (doc.security_settings && doc.security_settings.expiry_enabled) {
            const expiryDate = new Date(doc.security_settings.expiry_date);
            const today = new Date();
            expiryDate.setHours(23, 59, 59, 999);
            today.setHours(0, 0, 0, 0);
            if (today > expiryDate) {
              setIsExpired(true);
              setShowOpenLinkForm(false);
              return;
            }
          }
          const orgId = doc.org_id;
          if (orgId) {
            axios.get(`${baseUrl}/branding/public?orgId=${encodeURIComponent(orgId)}`)
              .then(br => {
                const b = br.data || {};
                setSignPageBranding({
                  product_name: b.product_name || 'SignTada',
                  logo_url: b.logo_url || null,
                  theme: b.theme || 'default',
                  primary_color: b.primary_color || '#FFD600',
                  accent_color: b.accent_color || '#FFAB00',
                  show_powered_by: b.show_powered_by !== false,
                  first_part_color: b.first_part_color ?? null,
                  rest_part_color: b.rest_part_color ?? null,
                });
              })
              .catch(() => {});
          }
        })
        .catch(err => {
          const msg = err.response?.data?.error || "Invalid or expired signing link.";
          setOpenLinkError(msg);
          setOpenLinkDoc(null);
        });
      return;
    }

    if (!token) return;

    // --- NORMAL FLOW: token present ---
    axios.get(`${baseUrl}/requests/get_document_by_reference/${referenceId}`)
      .then(response => {
        const doc = response.data;
        const currentRecipient = doc.recipients.find(r => r.sign_link === token);

        if (!currentRecipient) {
          alert("Invalid or expired signing link.");
          return;
        }

        // --- CHECK EXPIRY ---
        if (doc.security_settings && doc.security_settings.expiry_enabled) {
          const expiryDate = new Date(doc.security_settings.expiry_date);
          const today = new Date();
          expiryDate.setHours(23, 59, 59, 999);
          today.setHours(0, 0, 0, 0);

          if (today > expiryDate) {
            setIsExpired(true);
            return;
          }
        }

        // --- CHECK OTP REQUIREMENT ---
        if (doc.security_settings && doc.security_settings.otp_enabled) {
          setOtpEnabled(true);
          setOtpSending(true);
          axios.post(`${baseUrl}/otp/send`, {
            reference_id: referenceId,
            token: token
          })
            .then(() => {
              setOtpSending(false);
              setShowOtpModal(true);
            })
            .catch(error => {
              setOtpSending(false);
              console.error("Error sending OTP:", error);
              alert("Failed to send OTP. Please refresh the page.");
            });
        }

        if (doc.security_settings && doc.security_settings.capture_location) {
            triggerLocation(false);
        }

        if (currentRecipient.status === 'Signed') {
          setSigned(true);
          setShowReviewPage(false);
          setReviewAccepted(true);
        } else if (currentRecipient.status === 'Rejected') {
          setShowReviewPage(false);
          setReviewAccepted(false);
        } else {
          setShowReviewPage(true);
          setReviewAccepted(false);
        }

        setDocData({
          ...doc,
          recipient_email: currentRecipient.email,
          name: currentRecipient.name
        });

        const orgId = doc.org_id;
        if (orgId) {
          axios.get(`${baseUrl}/branding/public?orgId=${encodeURIComponent(orgId)}`)
            .then(br => {
              const b = br.data || {};
              const next = {
                product_name: b.product_name || 'SignTada',
                logo_url: b.logo_url || null,
                theme: b.theme || 'default',
                primary_color: b.primary_color || '#FFD600',
                accent_color: b.accent_color || '#FFAB00',
                show_powered_by: b.show_powered_by !== false,
                first_part_color: b.first_part_color ?? null,
                rest_part_color: b.rest_part_color ?? null,
              };
              setSignPageBranding(next);
              document.documentElement.style.setProperty('--primary', next.primary_color);
              document.documentElement.style.setProperty('--primary-dark', next.accent_color || next.primary_color);
              if (next.accent_color) document.documentElement.style.setProperty('--accent-color', next.accent_color);
            })
            .catch(() => {});
        }

        let fileToLoad = doc.last_signed_file_path || doc.file_path;
        const proxyUrl = `${baseUrl}/files/uploads/${fileToLoad}`;
        setPdfUrl(proxyUrl);
      })
      .catch(error => {
        console.error("Error loading document:", error);
      });
  }, [referenceId, token, linkToken]);

  const handleOpenLinkClaim = () => {
    const name = (openLinkName || "").trim();
    const email = (openLinkEmail || "").trim().toLowerCase();
    if (!name || !email) {
      setOpenLinkError("Please enter your name and email.");
      return;
    }
    if (!email.includes("@") || !email.includes(".")) {
      setOpenLinkError("Please enter a valid email address.");
      return;
    }
    setOpenLinkError("");
    setOpenLinkClaiming(true);
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    axios.post(`${baseUrl}/signing/claim_open_link`, {
      reference_id: referenceId,
      open_link_token: linkToken,
      name,
      email,
    })
      .then(res => {
        const signToken = res.data.token;
        navigate(`/sign/${referenceId}?token=${signToken}`, { replace: true });
      })
      .catch(err => {
        setOpenLinkClaiming(false);
        setOpenLinkError(err.response?.data?.error || "Failed to continue. Please try again.");
      });
  };

  // --- MANUAL LOCATION TRIGGER (Fixes the "Not Asking" issue) ---
  const triggerLocation = (isManualClick = false) => {
    if (!("geolocation" in navigator)) {
        if(isManualClick) alert("Geolocation is not supported by this browser.");
        return;
    }

    const geoOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationStatus("success");
        },
        (error) => {
          console.warn("Location warning:", error.message);
          // Only show "Denied" if the user explicitly clicked the button or explicitly blocked it
          if (error.code === 1) setLocationStatus("denied");
          if (isManualClick && error.code !== 1) alert("Could not fetch location. Ensure GPS is on and try again.");
        },
        geoOptions
    );
  };

  // --- INITIALIZE MEDIAPIPE FACE DETECTOR ---
  useEffect(() => {
    const initializeFaceDetector = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
        );
        const detector = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite`,
            delegate: "GPU"
          },
          runningMode: "VIDEO"
        });
        setFaceDetector(detector);
        setDetectionMsg("Position face in the circle");
      } catch (error) {
        console.error("AI Load Error:", error);
        setDetectionMsg("System Error: Could not load AI");
      }
    };

    if (showFaceModal && !faceDetector) {
      initializeFaceDetector();
    }
  }, [showFaceModal, faceDetector]);


  // --- REAL-TIME DETECTION LOOP ---
  const handleVideoOnPlay = () => {
    if (detectionInterval.current) clearInterval(detectionInterval.current);

    detectionInterval.current = setInterval(() => {
      if (
        faceDetector &&
        webcamRef.current &&
        webcamRef.current.video &&
        webcamRef.current.video.readyState === 4
      ) {
        const video = webcamRef.current.video;
        const startTimeMs = performance.now();
        const detections = faceDetector.detectForVideo(video, startTimeMs).detections;

        if (detections.length > 0) {
          const detection = detections[0];
          const box = detection.boundingBox;
          const vWidth = video.videoWidth;
          const vHeight = video.videoHeight;
          const faceCenterX = box.originX + (box.width / 2);
          const faceCenterY = box.originY + (box.height / 2);
          const minX = vWidth * 0.30;
          const maxX = vWidth * 0.70;
          const minY = vHeight * 0.20;
          const maxY = vHeight * 0.80;
          const isCentered = (faceCenterX > minX && faceCenterX < maxX) && (faceCenterY > minY && faceCenterY < maxY);
          const isCloseEnough = box.width > (vWidth * 0.15);

          if (isCentered && isCloseEnough) {
            setIsFaceDetected(true);
            setDetectionMsg("Face Aligned.");
          } else {
            setIsFaceDetected(false);
            if (!isCloseEnough) setDetectionMsg("Move Closer");
            else setDetectionMsg("Center your face in the oval");
          }
        } else {
          setIsFaceDetected(false);
          setDetectionMsg("No face detected.");
        }
      }
    }, 200);
  };

  useEffect(() => {
    if (!showFaceModal && detectionInterval.current) {
      clearInterval(detectionInterval.current);
      setIsFaceDetected(false);
    }
    return () => {
      if (detectionInterval.current) clearInterval(detectionInterval.current);
    };
  }, [showFaceModal]);


  const captureFace = useCallback(() => {
    if (!isFaceDetected) {
      alert("No face detected! Please ensure your face is visible and centered.");
      return;
    }
    const imageSrc = webcamRef.current.getScreenshot();
    setFaceImage(imageSrc);
    setShowFaceModal(false);
  }, [webcamRef, isFaceDetected]);

  const handleVerifyOtp = () => {
    if (!otpInput || otpInput.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setOtpVerifying(true);
    setOtpError("");
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];

    axios.post(`${baseUrl}/otp/verify`, {
      email: docData?.recipient_email,
      reference_id: referenceId,
      otp: otpInput
    })
      .then(response => {
        setOtpVerifying(false);
        if (response.data.success) {
          setOtpVerified(true);
          setShowOtpModal(false);
          setOtpInput("");
        } else {
          setOtpError("Invalid OTP. Please try again.");
        }
      })
      .catch(error => {
        setOtpVerifying(false);
        console.error("Error verifying OTP:", error);
        setOtpError("Invalid OTP. Please try again.");
      });
  };

  const handleResendOtp = () => {
    setOtpSending(true);
    setOtpError("");
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];

    axios.post(`${baseUrl}/otp/send`, {
      reference_id: referenceId,
      token: token
    })
      .then(() => {
        setOtpSending(false);
        alert("OTP sent successfully!");
      })
      .catch(error => {
        setOtpSending(false);
        console.error("Error sending OTP:", error);
        alert("Failed to send OTP. Please try again.");
      });
  };

  // Handle Accept - proceed to signing
  const handleAcceptReview = () => {
    setReviewAccepted(true);
    setShowReviewPage(false);
  };

  // Handle Reject - show rejection modal
  const handleRejectReview = () => {
    setShowRejectModal(true);
  };

  // Submit rejection with reason
  const handleSubmitRejection = () => {
    setRejectionError('');
    
    if (!rejectReason.trim()) {
      setRejectionError("Please provide a reason for rejecting the document.");
      return;
    }

    setIsRejecting(true);
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    axios.post(`${baseUrl}/signing/reject_document`, {
      reference_id: referenceId,
      token: token,
      reason: rejectReason.trim()
    })
      .then(() => {
        setIsRejecting(false);
        setShowRejectModal(false);
        setShowReviewPage(false);
        // Refresh document data to show rejected status
        const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
        axios.get(`${baseUrl}/requests/get_document_by_reference/${referenceId}`)
          .then(response => {
            const doc = response.data;
            const currentRecipient = doc.recipients.find(r => r.sign_link === token);
            setDocData({
              ...doc,
              recipient_email: currentRecipient.email,
              name: currentRecipient.name
            });
          })
          .catch(err => console.error("Error refreshing document:", err));
      })
      .catch(error => {
        setIsRejecting(false);
        console.error("Error rejecting document:", error);
        setRejectionError(error.response?.data?.error || "Error rejecting document. Please try again.");
      });
  };

  const handleSubmitSignature = () => {
    // 0. Check OTP Verification if enabled
    if (otpEnabled && !otpVerified) {
      setShowOtpModal(true);
      alert("Please verify OTP before signing the document.");
      return;
    }

    let signatureData;
    if (signingMethod === 'manual') {
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        const canvas = signatureRef.current.getCanvas();
        signatureData = canvas.toDataURL("image/png");
      } else {
        alert("Please draw your signature.");
        return;
      }
    } else {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 300;
      canvas.height = 100;
      ctx.font = isItalic ? "italic 30px Arial" : "30px Arial";
      ctx.fillStyle = "black";
      ctx.fillText(docData?.name || "Signed", 50, 60);
      signatureData = canvas.toDataURL("image/png");
    }

    // Validation
    if (docData?.security_settings?.face_capture && !faceImage) {
        setShowFaceModal(true);
        return;
    }
    if (docData?.security_settings?.capture_location && !locationCoords.latitude) {
        alert("Location is required for this document. Please click 'Enable Location'.");
        return;
    }

    setIsSubmitting(true);
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    axios.post(`${baseUrl}/signing/sign_document`, {
      reference_id: referenceId,
      token: token,
      signature: signatureData,
      latitude: locationCoords.latitude,
      longitude: locationCoords.longitude,
      face_image: faceImage
    })
      .then((response) => {
        setIsSubmitting(false);
        setSigned(true);
        if (response.data.signed_pdf) {
          setPdfUrl(response.data.signed_pdf);
        }
      })
      .catch(error => {
        setIsSubmitting(false);
        console.error("Error signing document:", error);
        alert("Error submitting signature. Please try again.");
      });
  };

  const modalStyles = `
    .face-modal-overlay {
        position: fixed; top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0,0,0,0.85); z-index: 2000;
        display: flex; align-items: center; justify-content: center;
        backdrop-filter: blur(5px);
    }
    .face-modal-content {
        background: white; padding: 25px; border-radius: 16px;
        width: 90%; max-width: 480px; text-align: center;
        position: relative;
        box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    }
    .webcam-wrapper {
        position: relative; width: 100%; height: 320px; margin: 20px 0; border-radius: 12px;
        overflow: hidden; background: #000; display: flex; align-items: center; justify-content: center;
    }
    .react-webcam { width: 100%; height: 100%; object-fit: cover; }
    .face-guide-overlay {
        position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
        width: 160px; height: 230px; border: 4px dashed rgba(255, 255, 255, 0.5);
        border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
        box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6); transition: all 0.3s ease; z-index: 10; pointer-events: none;
    }
    .face-guide-overlay.detected { border-color: #10B981; border-style: solid; box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7); }
    .status-pill {
        position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%);
        background: rgba(0,0,0,0.6); color: white; padding: 6px 14px;
        border-radius: 20px; font-size: 13px; font-weight: 600; z-index: 20; white-space: nowrap;
    }
    .verification-box {
        margin-top: 15px; padding: 12px; border-radius: 8px; font-size: 14px;
        display: flex; align-items: center; justify-content: space-between;
        border: 1px solid transparent; transition: all 0.2s;
    }
    .verification-box.pending { background: #fff7ed; border-color: #fdba74; color: #9a3412; }
    .verification-box.success { background: #f0fdf4; border-color: #86efac; color: #166534; }
    .verification-box.error { background: #fef2f2; border-color: #fecaca; color: #991b1b; }

    .btn-verify {
        background: #000; color: #fff; padding: 6px 12px; border-radius: 6px;
        font-size: 12px; font-weight: 600; cursor: pointer; border: none;
    }
    .btn-verify:hover { background: #333; }
    /* OTP Modal Styles */
    .otp-modal-content {
        background: white; padding: 30px; border-radius: 16px;
        width: 90%; max-width: 420px; text-align: center;
        position: relative;
        box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    }
    .otp-input {
        width: 100%;
        padding: 15px;
        font-size: 24px;
        text-align: center;
        letter-spacing: 8px;
        border: 2px solid #e2e8f0;
        border-radius: 8px;
        margin: 20px 0;
        font-weight: 600;
        transition: border-color 0.2s;
    }
    .otp-input:focus {
        outline: none;
        border-color: #FFD600;
    }
    .otp-input.error {
        border-color: #ef4444;
    }
    .otp-error {
        color: #ef4444;
        font-size: 13px;
        margin-top: -10px;
        margin-bottom: 10px;
    }
    .otp-resend {
        color: #3b82f6;
        background: none;
        border: none;
        cursor: pointer;
        font-size: 13px;
        text-decoration: underline;
        margin-top: 10px;
    }
    .otp-resend:disabled {
        color: #94a3b8;
        cursor: not-allowed;
    }
  `;

  if (isExpired) {
    return (
      <div style={{ background: '#ffffff', minHeight: '100vh', padding: 24 }}>
        <div className="step-container" style={{ textAlign: 'center', marginTop: 50, color: '#171717' }}>
          <h2 style={{ color: '#dc2626' }}>⚠️ Document Expired</h2>
          <p>This document is no longer available for signing.</p>
        </div>
      </div>
    );
  }

  if (openLinkError && !openLinkDoc) {
    return (
      <div style={{ background: '#ffffff', minHeight: '100vh', padding: 24 }}>
        <div className="step-container" style={{ textAlign: 'center', marginTop: 50, padding: 24, color: '#171717' }}>
          <h2 style={{ color: '#dc2626' }}>Invalid or expired link</h2>
          <p>{openLinkError}</p>
        </div>
      </div>
    );
  }

  if (showOpenLinkForm && openLinkDoc) {
    const docName = openLinkDoc.document_name || "Document";
    return (
      <div className="App" style={{ background: '#ffffff', minHeight: '100vh' }}>
        <div className="sign-page-brand-header" style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 18, color: signPageBranding.primary_color || '#FFD600' }}>{signPageBranding.product_name || 'SignTada'}</span>
        </div>
        <div className="step-container" style={{ maxWidth: 440, margin: '40px auto', padding: 32, background: '#ffffff', borderRadius: 16, border: '1px solid #e5e7eb' }}>
          <h2 style={{ marginBottom: 8, fontSize: 22, color: '#171717' }}>Sign this document</h2>
          <p style={{ color: '#737373', marginBottom: 24 }}>{docName}</p>
          <p style={{ marginBottom: 20, fontSize: 14, color: '#404040' }}>Enter your name and email to continue. This will be recorded with your signature.</p>
          {openLinkError && <p style={{ color: '#ef4444', marginBottom: 16, fontSize: 14 }}>{openLinkError}</p>}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#171717' }}>Full name</label>
            <input
              type="text"
              className="input-field"
              placeholder="Your name"
              value={openLinkName}
              onChange={e => setOpenLinkName(e.target.value)}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#171717' }}>Email</label>
            <input
              type="email"
              className="input-field"
              placeholder="you@example.com"
              value={openLinkEmail}
              onChange={e => setOpenLinkEmail(e.target.value)}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={openLinkClaiming}
            onClick={handleOpenLinkClaim}
            style={{ width: '100%', padding: 14 }}
          >
            {openLinkClaiming ? <SigningLoader size="sm" label="" /> : "Continue to sign"}
          </button>
        </div>
      </div>
    );
  }

  if (linkToken && !token) {
    return <div className="loader-container" style={{ background: '#ffffff', color: '#171717' }}><SigningLoader size="lg" label="Loading..." /></div>;
  }

  if (!docData) {
    return <div className="loader-container" style={{ background: '#ffffff', color: '#171717' }}><SigningLoader size="lg" label="Loading document..." /></div>;
  }

  const sigPositions = docData.sig_positions || {};
  const selectedPages = docData.selected_pages ?? [1];
  const applyAllPages = docData.apply_all_pages;
  const renderedPageWidth = 500;

  if (signed) {
    return <PromotionPage pdfUrl={pdfUrl} docName={docData?.name || 'Document'} />;
  }

  return (
    <div className="App" style={{ background: '#ffffff', minHeight: '100vh' }}>
      <style>{modalStyles}</style>

      {/* White-label header */}
      <div className="sign-page-brand-header" style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        {signPageBranding.logo_url ? (
          <img
            src={getBrandingLogoUrl(signPageBranding.logo_url)}
            alt={signPageBranding.product_name}
            style={{ maxHeight: 28, maxWidth: 120, objectFit: 'contain' }}
          />
        ) : (
          (() => {
            const name = (signPageBranding.product_name || 'SignTada').trim();
            let first, rest;
            if (name === 'SignTada') {
              first = 'Sign';
              rest = 'Tada';
            } else {
              const parts = name.split(/\s+/);
              first = parts[0] || '';
              rest = parts.slice(1).join(' ');
            }
            const firstColor = signPageBranding.first_part_color || '#171717';
            const restColor = signPageBranding.rest_part_color || signPageBranding.primary_color || '#FFD600';
            return (
              <span style={{ fontWeight: 700, fontSize: 18, display: 'inline-flex' }}>
                <span style={{ color: firstColor }}>{first}</span>
                {rest && <span style={{ color: restColor }}>{rest}</span>}
              </span>
            );
          })()
        )}
      </div>

      {/* FACE CAPTURE MODAL */}
      {showFaceModal && (
          <div className="face-modal-overlay">
              <div className="face-modal-content">
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 15}}>
                      <div style={{textAlign:'left'}}>
                          <h3 style={{margin:0, fontSize: 18}}>Identity Verification</h3>
                          <p style={{margin:0, fontSize: 12, color: '#666'}}>Position your face inside the oval</p>
                      </div>
                      <button onClick={() => setShowFaceModal(false)} style={{border:'none', background:'transparent', fontSize: 24, cursor:'pointer', color:'#666'}}><FiX /></button>
                  </div>
                  <div className="webcam-wrapper">
                      {faceDetector ? (
                          <>
                            <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" className="react-webcam" videoConstraints={{ facingMode: "user" }} onUserMedia={handleVideoOnPlay} />
                            <div className={`face-guide-overlay ${isFaceDetected ? 'detected' : ''}`}></div>
                            <div className="status-pill">{isFaceDetected ? <span style={{color: '#34d399', display:'flex', alignItems:'center', gap:5}}><FiCheckCircle /> Face Detected</span> : detectionMsg}</div>
                          </>
                      ) : (
                          <div style={{color:'white', display:'flex', flexDirection:'column', alignItems:'center'}}><SigningLoader size="md" label="Loading AI Models..." /></div>
                      )}
                  </div>
                  <button className={`btn ${isFaceDetected ? 'btn-primary' : 'btn-disabled'}`} style={{width:'100%'}} onClick={captureFace} disabled={!isFaceDetected}><FiCamera style={{marginRight: 8}}/> {isFaceDetected ? "Capture Photo" : "Waiting for face..."}</button>
              </div>
          </div>
      )}

      {/* OTP VERIFICATION MODAL */}
      {showOtpModal && (
          <div className="face-modal-overlay">
              <div className="otp-modal-content">
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 15}}>
                      <div style={{textAlign:'left'}}>
                          <h3 style={{margin:0, fontSize: 18, display:'flex', alignItems:'center', gap:8}}>
                            <FiLock color="#FFD600" /> OTP Verification
                          </h3>
                          <p style={{margin:0, fontSize: 12, color: '#666'}}>
                            Enter the 6-digit code sent to {docData?.recipient_email}
                          </p>
                      </div>
                      <button 
                        onClick={() => setShowOtpModal(false)} 
                        style={{border:'none', background:'transparent', fontSize: 24, cursor:'pointer', color:'#666'}}
                      >
                        <FiX />
                      </button>
                  </div>

                  <input
                    type="text"
                    className={`otp-input ${otpError ? 'error' : ''}`}
                    placeholder="000000"
                    maxLength={6}
                    value={otpInput}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setOtpInput(value);
                      setOtpError("");
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && otpInput.length === 6) {
                        handleVerifyOtp();
                      }
                    }}
                  />

                  {otpError && <div className="otp-error">{otpError}</div>}

                  <button
                    className="btn btn-primary"
                    style={{width:'100%', marginBottom: 10}}
                    onClick={handleVerifyOtp}
                    disabled={otpVerifying || otpInput.length !== 6}
                  >
                    {otpVerifying ? <SigningLoader size="sm" label="" /> : "Verify OTP"}
                  </button>

                  <button
                    className="otp-resend"
                    onClick={handleResendOtp}
                    disabled={otpSending}
                  >
                    {otpSending ? "Sending..." : "Resend OTP"}
                  </button>
              </div>
          </div>
      )}

      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <h1>{reviewAccepted ? 'Sign' : 'Review & Sign'}</h1>
        <p className="text-muted">
          {reviewAccepted 
            ? 'Please apply your signature to complete the document.' 
            : 'Please review the document below and accept or reject it before signing.'}
        </p>
      </div>

      {/* Review Page - Show before signing */}
      {showReviewPage && !reviewAccepted && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '30px',
          padding: '20px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          {/* PDF Preview for Review */}
          <div className="pdf-container" style={{
            position: 'relative',
            width: renderedPageWidth,
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            backgroundColor: 'white'
          }}>
            {pdfUrl && (
              <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={<div style={{ padding: 20, background: '#ffffff', color: '#171717' }}><SigningLoader size="md" label="Loading PDF..." /></div>}>
                <Page pageNumber={pageNumber} width={renderedPageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
            )}

            {/* Show signature placement indicator */}
            {docData?.sig_positions && Object.entries(docData.sig_positions).map(([pageStr, pagePositions]) => {
              const pageNum = parseInt(pageStr);
              const pos = pagePositions[docData?.recipient_email];
              return (
                pageNum === pageNumber && pos && (
                  <div key={pageStr} style={{
                    position: 'absolute',
                    top: pos.y,
                    left: pos.x,
                    width: '150px',
                    height: '50px',
                    backgroundColor: 'rgba(255, 214, 0, 0.2)',
                    border: '2px dashed #b45309',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#b45309',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    pointerEvents: 'none'
                  }}>
                    Your Signature Here
                  </div>
                )
              );
            })}

            <div className="pdf-navigation" style={{ padding: '10px', borderTop: '1px solid #f1f5f9' }}>
              <button className="btn btn-secondary btn-sm" disabled={pageNumber <= 1} onClick={() => setPageNumber(pageNumber - 1)}>Prev</button>
              <span className="page-info" style={{ margin: '0 10px' }}>Page {pageNumber} / {numPages}</span>
              <button className="btn btn-secondary btn-sm" disabled={pageNumber >= numPages} onClick={() => setPageNumber(pageNumber + 1)}>Next</button>
            </div>
          </div>

          {/* Accept/Reject Buttons */}
          <div style={{
            display: 'flex',
            gap: '20px',
            width: '100%',
            maxWidth: '500px',
            justifyContent: 'center'
          }}>
            <button
              className="btn btn-primary"
              style={{
                flex: 1,
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: 600,
                background: '#10B981',
                border: 'none'
              }}
              onClick={handleAcceptReview}
            >
              <FiCheckCircle style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Accept & Continue
            </button>
            <button
              className="btn btn-secondary"
              style={{
                flex: 1,
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: 600,
                background: '#EF4444',
                color: 'white',
                border: 'none'
              }}
              onClick={handleRejectReview}
            >
              <FiX style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Reject
            </button>
          </div>
        </div>
      )}

      {/* Rejection Message - Show if already rejected */}
      {!showReviewPage && !reviewAccepted && docData?.recipients?.find(r => r.sign_link === token)?.status === 'Rejected' && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: '#FEE2E2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <FiX size={40} color="#EF4444" />
          </div>
          <h2 style={{ color: '#EF4444', marginBottom: '12px' }}>Document Rejected</h2>
          <p style={{ color: '#64748b', fontSize: '16px', lineHeight: 1.6, marginBottom: '8px' }}>
            This document has been rejected.
          </p>
          {docData?.recipients?.find(r => r.sign_link === token)?.rejection_reason && (
            <div style={{
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '20px',
              width: '100%',
              textAlign: 'left'
            }}>
              <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px', fontWeight: 600 }}>REASON:</div>
              <div style={{ color: '#374151', fontSize: '14px' }}>
                {docData.recipients.find(r => r.sign_link === token).rejection_reason}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', color: '#EF4444', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FiX size={24} />
                Reject Document
              </h3>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px'
                }}
              >
                <FiX />
              </button>
            </div>
            <p style={{ marginBottom: '20px', color: '#64748b', lineHeight: 1.6 }}>
              Please provide a reason for rejecting this document. This will be shared with the sender.
            </p>
            {rejectionError && (
              <div style={{
                background: '#FEE2E2',
                border: '1px solid #EF4444',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#991B1B',
                fontSize: '14px'
              }}>
                {rejectionError}
              </div>
            )}
            <textarea
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
                setRejectionError('');
              }}
              placeholder="Enter rejection reason..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: rejectionError ? '1px solid #EF4444' : '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '20px'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                disabled={isRejecting}
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={handleSubmitRejection}
                disabled={isRejecting || !rejectReason.trim()}
                style={{
                  padding: '10px 20px',
                  background: '#EF4444',
                  color: 'white',
                  border: 'none'
                }}
              >
                {isRejecting ? 'Rejecting...' : 'Submit Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signing Page - Show after accepting review */}
      {!showReviewPage && reviewAccepted && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start',
          marginTop: '20px', gap: '40px', paddingBottom: '50px'
        }}>

          {/* PDF Viewer */}
          <div className="pdf-container" style={{
            position: 'relative', width: renderedPageWidth, border: '1px solid #e5e7eb',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderRadius: '8px', backgroundColor: 'white'
          }}>
            {pdfUrl && (
              <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={<div style={{ padding: 20, background: '#ffffff', color: '#171717' }}><SigningLoader size="md" label="Loading PDF..." /></div>}>
                <Page pageNumber={pageNumber} width={renderedPageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
            )}

            {(applyAllPages ? Array.from({ length: numPages }, (_, idx) => idx + 1) : selectedPages).map((page) => {
              const pos = sigPositions?.[String(page)]?.[docData?.recipient_email];
              return (
                page === pageNumber && pos && (
                  <div key={page} style={{
                    position: 'absolute', top: pos.y, left: pos.x, width: '150px', height: '50px',
                    backgroundColor: 'rgba(255, 214, 0, 0.2)', border: '2px dashed #b45309',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b45309',
                    fontWeight: 'bold', fontSize: '12px', pointerEvents: 'none'
                  }}>
                    Your Signature Here
                  </div>
                )
              );
            })}

            <div className="pdf-navigation" style={{ padding: '10px', borderTop: '1px solid #f1f5f9' }}>
              <button className="btn btn-secondary btn-sm" disabled={pageNumber <= 1} onClick={() => setPageNumber(pageNumber - 1)}>Prev</button>
              <span className="page-info" style={{ margin: '0 10px' }}>Page {pageNumber} / {numPages}</span>
              <button className="btn btn-secondary btn-sm" disabled={pageNumber >= numPages} onClick={() => setPageNumber(pageNumber + 1)}>Next</button>
            </div>
          </div>

          {/* Signature Controls */}
          <div className="step-container" style={{ minWidth: '350px', maxWidth: '400px' }}>
            <h3>Adopt Your Signature</h3>

          <div style={{ marginBottom: 15, display: 'flex', gap: 15 }}>
            <label className={`btn ${signingMethod === 'manual' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, justifyContent: 'center' }}>
              <input type="radio" name="signingMethod" value="manual" checked={signingMethod === 'manual'} onChange={() => setSigningMethod('manual')} style={{ display: 'none' }} />
              <FiEdit2 /> Draw
            </label>
            <label className={`btn ${signingMethod === 'auto' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, justifyContent: 'center' }}>
              <input type="radio" name="signingMethod" value="auto" checked={signingMethod === 'auto'} onChange={() => setSigningMethod('auto')} style={{ display: 'none' }} />
              <FiType /> Type
            </label>
          </div>

          {signingMethod === 'manual' ? (
            <div style={{ border: '1px solid #ccc', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
              <SignatureCanvas ref={signatureRef} penColor="black" canvasProps={{ width: 340, height: 140, className: 'sigCanvas' }} />
              <div style={{ borderTop: '1px solid #eee', padding: 5, textAlign: 'right' }}>
                <button className="icon-btn" style={{ color: '#dc2626', fontSize: 12, border: 'none', background: 'transparent', cursor: 'pointer' }} onClick={() => signatureRef.current.clear()}>Clear</button>
              </div>
            </div>
          ) : (
            <div className="auto-signature-preview" style={{ width: '100%', textAlign: 'center', padding: '30px 0' }}>
              <div style={{
                fontFamily: isItalic ? '"Times New Roman", Times, serif' : 'Arial, sans-serif',
                fontStyle: isItalic ? 'italic' : 'normal', fontSize: '32px',
                borderBottom: '1px solid #ccc', display: 'inline-block', padding: '0 10px'
              }}>
                {docData.name}
              </div>
              <div style={{ marginTop: 15 }}>
                <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={isItalic} onChange={() => setIsItalic(!isItalic)} /> Italicize</label>
              </div>
            </div>
          )}

          {/* OTP VERIFICATION INDICATOR */}
          {otpEnabled && (
              <div style={{marginTop: 20, padding: 10, background: otpVerified ? '#ecfdf5' : '#fffbeb', border: `1px solid ${otpVerified ? '#10b981' : '#fcd34d'}`, borderRadius: 6, display:'flex', alignItems:'center', gap: 10}}>
                  {otpVerified ? (
                      <>
                        <FiCheckCircle color="#10b981" size={20} />
                        <div>
                            <div style={{fontSize: 13, fontWeight: 700, color: '#065f46'}}>OTP Verified</div>
                            <div style={{fontSize: 11, color: '#064e3b'}}>Secure authentication complete</div>
                        </div>
                      </>
                  ) : (
                      <>
                        <FiLock color="#b45309" size={20} />
                        <div>
                            <div style={{fontSize: 13, fontWeight: 700, color: '#92400e'}}>OTP Required</div>
                            <div style={{fontSize: 11, color: '#b45309'}}>Check your email for code</div>
                        </div>
                        {!otpVerified && (
                          <button 
                            onClick={() => setShowOtpModal(true)}
                            style={{marginLeft: 'auto', background: '#b45309', color: 'white', border: 'none', borderRadius: 4, padding: '4px 8px', fontSize: 10, cursor: 'pointer'}}
                          >
                            Enter Code
                          </button>
                        )}
                      </>
                  )}
              </div>
          )}

          {/* --- UPDATED: PROFESSIONAL LOCATION BLOCK --- */}
          {docData?.security_settings?.capture_location && (
            <div className={`verification-box ${locationStatus === 'success' ? 'success' : locationStatus === 'denied' ? 'error' : 'pending'}`}>
                <div style={{display:'flex', alignItems:'center', gap: 10}}>
                    {locationStatus === 'success' ? <FiCheckCircle size={18} /> : locationStatus === 'denied' ? <FiShield size={18} /> : <FiMapPin size={18} />}
                    <div>
                        <div style={{fontWeight: 700}}>
                            {locationStatus === 'success' ? 'Location Verified' : locationStatus === 'denied' ? 'Location Denied' : 'Location Required'}
                        </div>
                        <div style={{fontSize: 11, opacity: 0.8}}>
                            {locationStatus === 'success' ? 'GPS data recorded for security' : 'Please enable location to sign'}
                        </div>
                    </div>
                </div>
                {locationStatus !== 'success' && (
                    <button className="btn-verify" onClick={() => triggerLocation(true)}>
                        {locationStatus === 'denied' ? 'Retry' : 'Enable'}
                    </button>
                )}
            </div>
          )}

          {/* --- UPDATED: PROFESSIONAL FACE ID BLOCK --- */}
          {docData?.security_settings?.face_capture && (
              <div className={`verification-box ${faceImage ? 'success' : 'pending'}`}>
                  <div style={{display:'flex', alignItems:'center', gap: 10}}>
                      {faceImage ? <FiCheckCircle size={18} /> : <FiCamera size={18} />}
                      <div>
                          <div style={{fontWeight: 700}}>{faceImage ? 'Identity Verified' : 'Face ID Required'}</div>
                          <div style={{fontSize: 11, opacity: 0.8}}>{faceImage ? 'Biometric capture complete' : 'Verification needed'}</div>
                      </div>
                  </div>
                  {faceImage ? (
                      <img src={faceImage} alt="Face" style={{width: 30, height: 30, borderRadius: '50%', objectFit: 'cover'}} />
                  ) : (
                      <button className="btn-verify" onClick={() => setShowFaceModal(true)}>Verify</button>
                  )}
              </div>
          )}

          <div style={{ marginTop: '20px', fontSize: '11px', color: '#737373', lineHeight: 1.4 }}>
            By clicking Submit, you agree to be legally bound by this document and the {signPageBranding.product_name} terms of service.
            {signPageBranding.product_name !== 'SignTada' && (
              <div style={{ marginTop: 8, fontSize: 10, opacity: 0.8 }}>Powered by SignTada</div>
            )}
          </div>

          <button
            className="btn btn-primary"
            style={{ marginTop: '20px', width: '100%' }}
            onClick={handleSubmitSignature}
            disabled={isSubmitting}
          >
            {isSubmitting ? <SigningLoader size="sm" label="" /> : "Submit Signature"}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

export default SignPage;